import { mapGetters, mapState } from 'vuex';
import moment from 'moment';

import formatterMixin from '@shared/mixins/formatterMixin';
import formatCycleRuntime from '@shared/lib/formatter/formatCycleRuntime';

const homeMixin = {
  mixins: [formatterMixin],
  computed: {
    ...mapState({
      branches: (state) => state.admin.branches,
      shTableSearch: (state) => state.systemHealth.shTableSearch,
      shTableOptions: (state) => state.systemHealth.shTableOptions,
      activeAlertTab: (state) => state.systemHealth.activeAlertTab,
      activeHealthTab: (state) => state.systemHealth.activeHealthTab,
      loadingTableData: (state) => state.systemHealth.loading,
      tableData: (state) => state.systemHealth.tableData,
      leakAlertCount: (state) => state.systemHealth.leakAlertCount,
      performanceAlertCount: (state) => state.systemHealth.performanceAlertCount,
      filterAlertCount: (state) => state.systemHealth.filterAlertCount,
      batteryAlertCount: (state) => state.systemHealth.batteryAlertCount,
      deviceAlertCount: (state) => state.systemHealth.deviceAlertCount,
      systemHealthTableHeaders: (state) => state.systemHealth.systemHealthTableHeaders,
      hvac: (state) => state.systemHealth.hvac,
      filters: (state) => state.systemHealth.filters,
      customer: (state) => state.systemHealth.customer,
      notifications: (state) => state.systemHealth.notifications,
      expandableLoading: (state) => state.systemHealth.expandableLoading,
      sortedCorporation: (state) => state.systemHealth.sortedCorporation,
      selectedCorp: (state) => state.systemHealth.selectedCorp,
      activeProfileTab: (state) => state.systemHealth.activeProfileTab,
      completedProfileCount: (state) => state.systemHealth.completedProfile.length,
      incompletedProfileCount: (state) => state.systemHealth.incompletedProfile.length,
      alertsCategory: (state) => state.systemHealth.alertsCategory,
      resetSystemHealthFlag: (state) => state.systemHealth.resetSystemHealthFlag,
      status: (state) => state.systemHealth.status,
      actionNote: (state) => state.systemHealth.actionNote,
      unmuteDate: (state) => state.systemHealth.unmuteDate,
      chronicAlerts: (state) => state.systemHealth.chronicAlerts,
      showActionModal: (state) => state.systemHealth.showActionModal,
      actionModalView: (state) => state.systemHealth.actionModalView,
      processing: (state) => state.systemHealth.processing,
    }),
    ...mapGetters([
      'isSmacAdmin',
      'isManufactureAdmin',
      'systemHealthTableRecordCount',
      'severeSystemCount',
      'mildSystemCount',
      'normalSystemCount',
      'allCount',
      'allBranches',
    ]),
  },
  methods: {
    getSystemAge(s) {
      if (s !== undefined) {
        return `${s} year${s > 1 || s === 0 ? 's' : ''}`;
      }
      return '--';
    },
    getFormattedDate(date) {
      if (date) {
        return moment(date).format('M-DD-YY | h:mm A');
      }
      return '--';
    },
    formattedDate(date) {
      if (date) {
        return moment(date).format('M/DD/YYYY');
      }
      return '--';
    },
    format(v) {
      if (v) {
        return v;
      }
      return '--';
    },
    toggleActionModal(show, view) {
      // eslint-disable-next-line no-restricted-globals
      event.stopPropagation();
      if (!show) {
        this.$store.dispatch('changeHomeValue', {
          field: 'showActionModal',
          value: show,
        });
        this.$store.dispatch('changeHomeValue', {
          field: 'actionModalView',
          value: '',
        });
      } else {
        this.$store.dispatch('changeHomeValue', {
          field: 'showActionModal',
          value: show,
        });
        this.$store.dispatch('changeHomeValue', {
          field: 'actionModalView',
          value: view,
        });
      }
    },
    checkAlerts(noti, type) {
      const { alerts } = noti;
      let hasAlert = false;
      alerts.forEach((alert) => {
        const { category } = alert;
        if (type === 'device' && category === 'Device') {
          hasAlert = true;
        }
        if (type === 'filter' && category === 'Filter') {
          hasAlert = true;
        }
        if (type === 'performance' && category === 'Performance') {
          hasAlert = true;
        }
        if (type === 'leak' && category === 'Leak') {
          hasAlert = true;
        }
      });
      return hasAlert;
    },
    changeHeaderFilter(header, filter) {
      this.$store.dispatch('setHeaderFilter', {
        header,
        filter,
      });
    },
    async changePartner(corpId) {
      this.$store.dispatch('changeHomeValue', {
        field: 'selectedCorp',
        value: corpId,
      });
      await this.$store.dispatch('getSystemHealth');
    },
    deviceStatus(s) {
      if (!s) {
        return 'Offline';
      }
      return s;
    },
    getOccurrence(alerts, type) {
      if (type === 'leak') {
        return alerts.leakCount;
      }
      if (type === 'filter') {
        return alerts.filterCount;
      }
      if (type === 'device') {
        return alerts.deviceCount;
      }
      if (type === 'performance') {
        return alerts.performanceCount;
      }
      return alerts.deviceCount + alerts.leakCount + alerts.filterCount + alerts.performanceCount;
    },
    getFilterSize(f) {
      const { width, height, thickness } = f;
      return `${width}x${height}x${thickness}`;
    },
    getDescription(a) {
      const {
        category, description, location, type, boundaryValue: value,
      } = a;

      if (type && (type.includes('Supply Air') || type.includes('Delta T'))) {
        const v = `${Math.round(value)}`;
        if (description.includes('{boundaryValue}')) {
          return description.replace('{boundaryValue}', v);
        }
      }
      if (type && type.includes('Runtime')) {
        const vInSeconds = Math.abs(value * 3600);
        const v = formatCycleRuntime(vInSeconds);
        if (description.includes('{boundaryValue}')) {
          return description.replace('{boundaryValue} hours', v);
        }
      }
      if (category === 'Leak' && type.includes('Water')) {
        if (location) {
          return `Water was detected in the ${location}`;
        }
        return 'Water was detected in the drain pan';
      }
      if (!description) {
        return 'No Alert Description';
      }
      return description;
    },
    getUnit(rowItem) {
      const {
        boundaryValue, type, category, location,
      } = rowItem;
      const value = boundaryValue > 0 ? Math.round(rowItem.boundaryValue) : boundaryValue;
      if (type === 'Filter Status') {
        return `${value} Pa`;
      }
      if (type === 'Filter Life Low') {
        return `${value * 100} %`;
      }
      if (category === 'Device') {
        return `${value} %`;
      }
      if (type === 'Rapid Pressure Change') {
        return `${value} Pa`;
      }
      if (type.includes('Runtime')) {
        const valueInSeconds = rowItem.boundaryValue * 3600;
        return formatCycleRuntime(valueInSeconds);
      }
      if (category === 'Performance') {
        return `${value} °F`;
      }
      if (category === 'Leak') {
        if (location) {
          return location;
        }
        return '--';
      }
      return value;
    },
  },
};

export default homeMixin;
